"use client";

import Link from "next/link";
import { useEffect, useMemo, useState } from "react";
import {
  ArrowRight,
  BadgeCheck,
  Camera,
  ChevronDown,
  Gift,
  MapPin,
  Smartphone,
  Store,
  Users,
} from "lucide-react";
import { IPhoneMockup } from "@/components/IPhoneMockup";
import { AppStoreDownloadButtons } from "@/components/marketing/AppStoreDownloadButtons";
import { MarketingFooter } from "@/components/marketing/MarketingFooter";
import { MarketingHeader } from "@/components/marketing/MarketingHeader";
import { RestaurantPricingSection } from "@/components/marketing/RestaurantPricingSection";
import { getScanShowcaseImagePath } from "@/lib/home-showcase-images";
import { useHomeShowcaseImageTheme } from "@/lib/useHomeShowcaseImageTheme";
import { useLanguage } from "@/lib/useLanguage";
import { supabase } from "@/lib/supabase";

const RESTAURANT_MONTHLY_PRICE_EUR = 14.99;
const RESTAURANT_ANNUAL_PRICE_EUR = 149.99;

type ClaimContextState = "idle" | "loading" | "ready" | "error";

interface RestaurantsLandingPageContentProps {
  claimRestaurantId?: string | null;
}

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const VALUE_PROP_ICONS = [Camera, MapPin, BadgeCheck, Store, BadgeCheck, Smartphone] as const;
const HERO_VALUE_PROP_ICONS = [Camera, MapPin, Gift] as const;

function fillTemplate(template: string, vars: Record<string, string | number>) {
  return Object.entries(vars).reduce(
    (text, [key, value]) => text.replace(new RegExp(`\\{\\{${key}\\}\\}`, "g"), String(value)),
    template
  );
}

export function RestaurantsLandingPageContent({ claimRestaurantId = null }: RestaurantsLandingPageContentProps) {
  const { language, t } = useLanguage();
  const imageTheme = useHomeShowcaseImageTheme();
  const heroImageSrc = getScanShowcaseImagePath("menu", language, imageTheme);
  const [claimContextState, setClaimContextState] = useState<ClaimContextState>("idle");
  const [claimRestaurantName, setClaimRestaurantName] = useState<string | null>(null);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  const pageT = t.restaurantsPage;

  const vars = useMemo(
    () => ({
      monthlyPrice: RESTAURANT_MONTHLY_PRICE_EUR,
      annualPrice: RESTAURANT_ANNUAL_PRICE_EUR,
    }),
    []
  );

  const heroChips = useMemo(
    () => [
      pageT.hero.chips.noCommissions,
      pageT.hero.chips.planMonthlyAnnual,
      pageT.hero.chips.platforms,
      pageT.hero.chips.availableAcrossSpain,
    ],
    [pageT]
  );

  const pageLinks = useMemo(
    () => [
      { href: "/", label: pageT.links.home },
      { href: `/${language}/menu-del-dia-barcelona`, label: pageT.links.barcelonaMenus },
      { href: "/download", label: pageT.links.download },
      { href: "/support", label: pageT.links.support },
      { href: "/privacy", label: pageT.links.privacy },
      { href: "/terms", label: pageT.links.terms },
    ],
    [language, pageT.links]
  );

  const validClaimRestaurantId = useMemo(
    () => (claimRestaurantId && UUID_REGEX.test(claimRestaurantId) ? claimRestaurantId : null),
    [claimRestaurantId]
  );

  const downloadHref = useMemo(() => {
    return validClaimRestaurantId
      ? `/download?restaurant_id=${encodeURIComponent(validClaimRestaurantId)}`
      : "/download";
  }, [validClaimRestaurantId]);

  const scrollToHowItWorks = () => {
    document.getElementById("como-funciona")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const scrollToHowToClaim = () => {
    document.getElementById("como-reclamar")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  useEffect(() => {
    if (!validClaimRestaurantId) {
      setClaimContextState("idle");
      setClaimRestaurantName(null);
      return;
    }

    let cancelled = false;
    setClaimContextState("loading");

    (async () => {
      const { data, error } = await supabase
        .from("restaurants")
        .select("name")
        .eq("id", validClaimRestaurantId)
        .is("deleted_at", null)
        .maybeSingle();

      if (cancelled) return;

      if (error) {
        setClaimContextState("error");
        setClaimRestaurantName(null);
        return;
      }

      const safeName = typeof data?.name === "string" && data.name.trim().length > 0 ? data.name.trim() : null;
      setClaimRestaurantName(safeName);
      setClaimContextState("ready");
    })();

    return () => {
      cancelled = true;
    };
  }, [validClaimRestaurantId]);

  const claimBannerMessage =
    claimRestaurantName != null
      ? fillTemplate(pageT.claim.bannerWithName, { name: claimRestaurantName })
      : pageT.claim.bannerGeneric;

  return (
    <div key={language} className="min-h-screen flex flex-col bg-white text-slate-900 dark:bg-slate-950 dark:text-slate-100">
      <MarketingHeader locale={language} />

      <main className="flex-1">
        <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-10 sm:py-12 pb-16">
          <section className="mb-14 lg:mb-16 lg:pb-4">
            <div className="grid grid-cols-1 gap-10 lg:gap-12 items-center lg:items-start lg:grid-cols-[minmax(0,1fr)_minmax(536px,0.95fr)] xl:grid-cols-[minmax(0,1fr)_minmax(584px,0.95fr)]">
              <div className="text-center lg:text-left">
                <p className="text-xs font-semibold uppercase tracking-wider text-blue-600 dark:text-blue-400 mb-3">
                  {pageT.hero.eyebrow}
                </p>
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 dark:text-white leading-[1.12] mb-4 max-w-xl mx-auto lg:mx-0">
                  {pageT.hero.title}
                </h1>
                <p className="text-base sm:text-lg text-slate-600 dark:text-slate-400 leading-relaxed mb-6 max-w-xl mx-auto lg:mx-0">
                  {fillTemplate(pageT.hero.subtitle, vars)}
                </p>

                <ul className="flex flex-wrap justify-center lg:justify-start gap-2 mb-6 max-w-xl mx-auto lg:mx-0">
                  {heroChips.map((chip) => (
                    <li
                      key={chip}
                      className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 dark:border-slate-700/60 dark:bg-slate-800/50 px-3 py-1.5 text-xs text-slate-700 dark:text-slate-300"
                    >
                      {chip}
                    </li>
                  ))}
                </ul>

                {claimRestaurantId && !validClaimRestaurantId && (
                  <div className="mb-6 rounded-xl border border-amber-300 bg-amber-50 dark:border-amber-500/30 dark:bg-amber-950/20 p-4 text-left max-w-xl mx-auto lg:mx-0">
                    <p className="text-sm text-amber-900 dark:text-amber-100/90 leading-relaxed">{pageT.claim.invalidLink}</p>
                  </div>
                )}

                {validClaimRestaurantId && (
                  <div className="mb-6 rounded-xl border border-blue-300 bg-blue-50 dark:border-blue-500/40 dark:bg-blue-950/40 p-4 sm:p-5 text-left max-w-xl mx-auto lg:mx-0">
                    {claimContextState === "loading" ? (
                      <p className="text-sm text-blue-800 dark:text-blue-100">{pageT.claim.loading}</p>
                    ) : (
                      <p className="text-sm text-blue-900 dark:text-blue-50 leading-relaxed">{claimBannerMessage}</p>
                    )}
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Link
                        href={downloadHref}
                        className="inline-flex items-center justify-center rounded-lg border border-blue-400 dark:border-blue-300/60 px-3 py-2 text-xs font-semibold text-blue-800 dark:text-blue-100 hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors"
                      >
                        {pageT.cta.claimRestaurant}
                      </Link>
                      <button
                        type="button"
                        onClick={scrollToHowToClaim}
                        className="inline-flex items-center justify-center rounded-lg bg-blue-600 px-3 py-2 text-xs font-semibold text-white hover:bg-blue-500 transition-colors"
                      >
                        {pageT.cta.howToClaim}
                      </button>
                    </div>
                  </div>
                )}

                <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start max-w-md mx-auto lg:mx-0">
                  <Link
                    href={downloadHref}
                    className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-semibold text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-slate-950"
                  >
                    {pageT.cta.startTrial}
                    <ArrowRight className="w-4 h-4 shrink-0" aria-hidden />
                  </Link>
                  <button
                    type="button"
                    onClick={scrollToHowItWorks}
                    className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl border border-slate-300 bg-white dark:border-slate-600 dark:bg-slate-800/40 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700/50 font-medium text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-slate-950"
                  >
                    {pageT.cta.seeHowItWorks}
                  </button>
                </div>

                <div
                  className="mt-8 lg:mt-10 max-w-xl mx-auto lg:mx-0 text-left"
                  aria-labelledby="hero-value-props-heading"
                >
                  <h2
                    id="hero-value-props-heading"
                    className="text-sm font-semibold text-slate-900 dark:text-white mb-3"
                  >
                    {pageT.heroValueProps.title}
                  </h2>
                  <ul className="space-y-2.5">
                    {pageT.heroValueProps.items.map((item, index) => {
                      const Icon = HERO_VALUE_PROP_ICONS[index] ?? Camera;
                      return (
                        <li
                          key={item.title}
                          className="flex items-start gap-3 rounded-lg border border-slate-200/80 bg-slate-50/80 dark:border-slate-700/50 dark:bg-slate-800/30 px-3.5 py-3"
                        >
                          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-emerald-600/10 text-emerald-600 dark:text-emerald-400">
                            <Icon className="w-4 h-4" aria-hidden />
                          </span>
                          <div className="min-w-0 pt-0.5">
                            <h3 className="text-sm font-semibold text-slate-900 dark:text-white leading-snug">
                              {item.title}
                            </h3>
                            <p className="mt-0.5 text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                              {item.description}
                            </p>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>

              <div className="w-full flex justify-center lg:justify-end max-w-full">
                <div className="w-max max-w-full lg:ml-auto">
                  <IPhoneMockup
                    src={heroImageSrc}
                    alt={pageT.hero.phoneAlt}
                    priority
                  />
                </div>
              </div>
            </div>
          </section>

          <section className="mb-14 lg:mb-16" aria-labelledby="valor-heading">
            <h2 id="valor-heading" className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white mb-2 text-center lg:text-left">
              {pageT.valueProps.title}
            </h2>
            <p className="text-slate-600 dark:text-slate-400 text-sm sm:text-base mb-8 max-w-2xl text-center lg:text-left mx-auto lg:mx-0">
              {pageT.valueProps.subtitle}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {pageT.valueProps.items.map((item, index) => {
                const Icon = VALUE_PROP_ICONS[index] ?? Camera;
                return (
                  <article
                    key={item.title}
                    className="rounded-xl bg-slate-50 border border-slate-200 dark:bg-slate-800/50 dark:border-slate-700/50 p-5 sm:p-6"
                  >
                    <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-600/15 text-emerald-600 dark:text-emerald-400 mb-3">
                      <Icon className="w-5 h-5" aria-hidden />
                    </span>
                    <h3 className="text-base font-semibold text-slate-900 dark:text-white mb-1.5">{item.title}</h3>
                    <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">{item.description}</p>
                  </article>
                );
              })}
            </div>
          </section>

          <RestaurantPricingSection pricing={pageT.pricing} onStartTrial={() => window.location.assign(downloadHref)} />

          <section
            id="como-reclamar"
            className="mb-14 lg:mb-16 scroll-mt-24"
            aria-labelledby="como-reclamar-heading"
          >
            <h2
              id="como-reclamar-heading"
              className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white mb-2 text-center lg:text-left"
            >
              {pageT.claimFlow.title}
            </h2>
            <p className="text-slate-600 dark:text-slate-400 text-sm sm:text-base mb-8 max-w-2xl text-center lg:text-left mx-auto lg:mx-0">
              {pageT.claimFlow.body}
            </p>

            <ol className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl mx-auto lg:mx-0">
              {pageT.claimFlow.steps.map((step, index) => (
                <li
                  key={step}
                  className="rounded-xl bg-slate-50 border border-slate-200 dark:bg-slate-800/50 dark:border-slate-700/50 p-5 sm:p-6 flex gap-4"
                >
                  <span
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-600/15 text-blue-700 dark:text-blue-300 tabular-nums font-semibold"
                    aria-hidden
                  >
                    {index + 1}
                  </span>
                  <div className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">{step}</div>
                </li>
              ))}
            </ol>

            <div className="mt-8 rounded-2xl border border-slate-200 bg-white dark:border-slate-700/60 dark:bg-slate-900/40 p-6 sm:p-8 max-w-3xl mx-auto lg:mx-0">
              <h3 className="text-lg sm:text-xl font-bold text-slate-900 dark:text-white">{pageT.claimFlow.ctaTitle}</h3>
              <p className="mt-2 text-sm text-slate-600 dark:text-slate-400 leading-relaxed max-w-2xl">
                {pageT.claimFlow.ctaBody}
              </p>
              <div className="mt-5">
                <AppStoreDownloadButtons className="max-w-md" />
              </div>
              <p className="mt-4 text-xs text-slate-500 dark:text-slate-500 leading-relaxed">
                {pageT.claimFlow.supportFallback}{" "}
                <Link href="/support" className="underline underline-offset-2 hover:text-slate-700 dark:hover:text-slate-200">
                  {pageT.links.support}
                </Link>
                .
              </p>
            </div>
          </section>

          <section
            id="como-funciona"
            className="mb-14 lg:mb-16 scroll-mt-24"
            aria-labelledby="como-funciona-heading"
          >
            <h2
              id="como-funciona-heading"
              className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white mb-2 text-center lg:text-left"
            >
              {pageT.howItWorks.title}
            </h2>
            <p className="text-slate-600 dark:text-slate-400 text-sm sm:text-base mb-8 max-w-2xl text-center lg:text-left mx-auto lg:mx-0">
              {pageT.howItWorks.subtitle}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {pageT.howItWorks.steps.map((step, index) => (
                <article
                  key={step.title}
                  className="rounded-xl bg-slate-50 border border-slate-200 dark:bg-slate-800/50 dark:border-slate-700/50 p-5 sm:p-6"
                >
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                    {pageT.howItWorks.stepLabel} {index + 1}
                  </span>
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-white mt-2 mb-2">{step.title}</h3>
                  <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">{step.description}</p>
                </article>
              ))}
            </div>
          </section>

          <section className="mb-14 lg:mb-16" aria-labelledby="comunidad-heading">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 dark:border-slate-700/50 dark:bg-slate-800/40 p-6 sm:p-8 flex flex-col sm:flex-row gap-6 items-start">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-blue-600/15 dark:bg-blue-600/20 text-blue-600 dark:text-blue-400">
                <Users className="w-6 h-6" aria-hidden />
              </span>
              <div>
                <h2 id="comunidad-heading" className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white mb-3">
                  {pageT.community.title}
                </h2>
                <p className="text-sm sm:text-base text-slate-700 dark:text-slate-300 leading-relaxed max-w-3xl">
                  {pageT.community.body}
                </p>
              </div>
            </div>
          </section>

          <section className="mb-14 lg:mb-16" aria-labelledby="faq-heading">
            <h2 id="faq-heading" className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white mb-6 text-center lg:text-left">
              {pageT.faq.title}
            </h2>
            <div className="space-y-2 max-w-3xl mx-auto lg:mx-0">
              {pageT.faq.items.map((item, index) => {
                const isOpen = openFaqIndex === index;
                return (
                  <div
                    key={item.q}
                    className="rounded-xl border border-slate-200 bg-white dark:border-slate-700/50 dark:bg-slate-800/40 overflow-hidden"
                  >
                    <button
                      type="button"
                      onClick={() => setOpenFaqIndex(isOpen ? null : index)}
                      className="w-full flex items-center justify-between gap-4 px-4 sm:px-5 py-4 text-left text-sm font-semibold text-slate-900 dark:text-white hover:bg-slate-50 dark:hover:bg-slate-800/60 transition-colors"
                      aria-expanded={isOpen}
                    >
                      {item.q}
                      <ChevronDown
                        className={`w-5 h-5 shrink-0 text-slate-500 dark:text-slate-400 transition-transform ${isOpen ? "rotate-180" : ""}`}
                        aria-hidden
                      />
                    </button>
                    {isOpen ? (
                      <p className="px-4 sm:px-5 pb-4 text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                        {fillTemplate(item.a, vars)}
                      </p>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </section>

          <section className="mb-14 lg:mb-16 text-center lg:text-left" aria-label={pageT.cta.showMenuFree}>
            <Link
              href={downloadHref}
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-semibold text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-slate-950"
            >
              {pageT.cta.showMenuFree}
              <ArrowRight className="w-4 h-4 shrink-0" aria-hidden />
            </Link>
          </section>

          <nav
            className="flex flex-wrap justify-center lg:justify-start gap-x-4 gap-y-2 text-sm text-slate-500 mb-8"
            aria-label={pageT.links.navAria}
          >
            {pageLinks.map((link) => (
              <Link key={link.href} href={link.href} className="hover:text-slate-700 dark:hover:text-slate-300 transition-colors">
                {link.label}
              </Link>
            ))}
          </nav>
        </div>
      </main>

      <MarketingFooter />
    </div>
  );
}
